// One-phone pass-and-play: the full CompleteMasti game with every seat on one phone. The server runs
// the game and sends only the table (hands, the prompt, scores), never the deck. The phone keeps the
// room code and owner token so a refresh picks the game back up.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { trackEvent } from '../../lib/analytics';
import { Link } from 'react-router';
import type { PlayerState, RoomState } from '@shared/engine';
import { ONE_PHONE_MAX, ONE_PHONE_MIN, checkNames, screenFor, type LocalScreen, type LocalUi } from '@shared/passplay';
import { NAME_MAX, WRITE_IN_MAX } from '@shared/rules';
import type { CardData, PackSummary, SoloMove, SubmitChoice } from '@shared/types';
import { buildView } from '@shared/view';
import { AnswerCard, FaceDownCard, FilledPrompt, PromptCard } from '../../components/Cards';
import { Avatar, Scorebar, useDocumentTitle } from '../../components/Chrome';
import { usePrefs } from '../../components/Prefs';
import { Sheet } from '../../components/Sheet';
import { api } from '../../lib/api';
import { call, getSocket } from '../../lib/socket';
import { gameInfo } from './info';
import { Choice, useStoredState, useWakeLock } from './kit';

const info = gameInfo('one-phone');
const SAVE_KEY = 'cm:onephone:game';

/** The server runs the game; the phone keeps just enough to pick it back up after a refresh. */
interface Saved {
  code: string;
  ownerToken: string;
  introRound: number;
}

interface Setup {
  names: string[];
  packCodes: string[];
  extraPacks: PackSummary[];
  pointsToWin: number;
  handSize: number;
  writeIns: number;
}

const DEFAULT_SETUP: Setup = { names: ['', '', ''], packCodes: [], extraPacks: [], pointsToWin: 5, handSize: 7, writeIns: 1 };

function readSaved(): Saved | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    const saved = raw ? (JSON.parse(raw) as Saved) : null;
    return saved?.code && saved.ownerToken ? saved : null;
  } catch {
    return null;
  }
}

function writeSaved(saved: Saved | null) {
  try {
    if (saved) localStorage.setItem(SAVE_KEY, JSON.stringify(saved));
    else localStorage.removeItem(SAVE_KEY);
  } catch {
    // Storage blocked: the game carries on, it just won't survive a refresh.
  }
}

export function OnePhone() {
  useDocumentTitle('One phone');
  const { toast } = usePrefs();
  const [mode, setMode] = useState<'loading' | 'setup' | 'playing' | 'lost'>('loading');
  const [state, setState] = useState<RoomState | null>(null);
  const [saved, setSavedGame] = useState<Saved | null>(readSaved);
  const [ui, setUi] = useState<LocalUi>({ introRound: saved?.introRound ?? 0, unlocked: null });
  const [menu, setMenu] = useState(false);

  // Pick the game back up after a refresh or a dropped connection. The phone always comes back
  // covered, so a refresh never shows anyone's hand.
  const resume = useCallback(async () => {
    const current = readSaved();
    if (!current) return setMode((m) => (m === 'loading' ? 'setup' : m));
    const res = await call('solo:resume', { code: current.code, ownerToken: current.ownerToken });
    if (res.ok) {
      setState(res.state);
      setMode('playing');
    } else {
      writeSaved(null);
      setSavedGame(null);
      setMode('lost');
    }
  }, []);

  useEffect(() => {
    const socket = getSocket();
    socket.on('connect', resume);
    if (socket.connected) void resume();
    return () => {
      socket.off('connect', resume);
    };
  }, [resume]);

  useEffect(() => {
    if (saved) writeSaved({ ...saved, introRound: ui.introRound });
  }, [saved, ui.introRound]);
  useWakeLock(mode === 'playing');

  const act = useCallback(
    async (move: SoloMove, after?: Partial<LocalUi>) => {
      const res = await call('solo:act', move);
      if (!res.ok) {
        toast(res.error, 'error');
        return false;
      }
      setState(res.state);
      if (after) setUi((u) => ({ ...u, ...after }));
      return true;
    },
    [toast],
  );

  async function begin(setup: { names: string[]; packCodes: string[]; pointsToWin: number; handSize: number; writeIns: number }) {
    const res = await call('solo:create', setup);
    if (!res.ok) throw new Error(res.error);
    const next = { code: res.code, ownerToken: res.ownerToken, introRound: 0 };
    writeSaved(next);
    setSavedGame(next);
    setState(res.state);
    setUi({ introRound: 0, unlocked: null });
    setMode('playing');
  }

  function quit() {
    writeSaved(null);
    setSavedGame(null);
    setState(null);
    setMode('setup');
  }

  const screen: LocalScreen | null = state && mode === 'playing' ? screenFor(state, ui) : null;
  const screenKey = screen ? `${screen.kind}:${'player' in screen ? screen.player.id : ''}:${state?.round?.number}` : mode;
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [screenKey]);

  if (mode === 'loading') return <main className="page center muted">Finding your game...</main>;
  if (mode === 'setup') return <SetupScreen onStart={begin} />;
  if (mode === 'lost' || !state || !screen) {
    return (
      <main className="page stack center" style={{ paddingTop: '14vh' }}>
        <h1>That game has ended</h1>
        <p className="muted">Games are kept for a few hours after the last move. Deal a fresh one.</p>
        <button type="button" className="btn btn-primary btn-lg" onClick={quit}>
          New game
        </button>
      </main>
    );
  }

  const s = state;
  const view = buildView(s, null, [], Date.now());
  const inRound = s.phase !== 'game_over';

  return (
    <div className="game mg onephone">
      <header className="game-header">
        <div className="inner">
          <div className="row between" style={{ flexWrap: 'nowrap' }}>
            <Link to="/games" className="btn btn-ghost btn-sm" aria-label="Back to all games">
              ← Games
            </Link>
            <span className="grow" />
            {inRound && s.round && <span className="chip blue">Round {s.round.number}</span>}
            {s.settings.pointsToWin > 0 && <span className="chip">First to {s.settings.pointsToWin}</span>}
            <button type="button" className="icon-btn" aria-label="Game menu" onClick={() => setMenu(true)}>
              ☰
            </button>
          </div>
          <Scorebar players={view.players.map((p) => ({ ...p, isHost: false }))} />
        </div>
      </header>

      {screen.kind === 'intro' && <Intro state={s} judge={screen.judge} onGo={() => setUi({ introRound: s.round!.number, unlocked: null })} />}
      {screen.kind === 'handoff' && <Handoff state={s} player={screen.player} role={screen.role} onUnlock={() => setUi((u) => ({ ...u, unlocked: screen.player.id }))} />}
      {screen.kind === 'play' && (
        <PlayTurn
          key={`${screen.player.id}:${s.round?.number}`}
          state={s}
          player={screen.player}
          onSubmit={(choices) => act({ action: 'submit', playerId: screen.player.id, choices }, { unlocked: null })}
          onRedraw={() => act({ action: 'redraw', playerId: screen.player.id })}
          onCover={() => setUi((u) => ({ ...u, unlocked: null }))}
        />
      )}
      {screen.kind === 'judge' && (
        <JudgeTurn
          state={s}
          judge={screen.player}
          onReveal={(id) => act({ action: 'reveal', playerId: screen.player.id, submissionId: id })}
          onPick={(id) => act({ action: 'pick', playerId: screen.player.id, submissionId: id }, { unlocked: null })}
        />
      )}
      {screen.kind === 'round_over' && <RoundOver state={s} onNext={() => act({ action: 'next' }, { unlocked: null })} />}
      {screen.kind === 'game_over' && (
        <GameOverScreen
          state={s}
          onAgain={() =>
            act({ action: 'again' }, { introRound: 0, unlocked: null })
          }
          onNew={quit}
        />
      )}
      {screen.kind === 'stuck' && (
        <main className="game-main mg-main">
          <p className="notice">This round got stuck. End the game to see the scores.</p>
          <button type="button" className="btn btn-primary btn-lg" onClick={() => act({ action: 'end' })}>
            End the game
          </button>
        </main>
      )}

      {menu && (
        <Sheet title="One phone" onClose={() => setMenu(false)}>
          <div className="menu-list">
            {inRound && (
              <button
                type="button"
                onClick={() => {
                  setMenu(false);
                  if (window.confirm('End the game now? The top scorer wins.')) act({ action: 'end' }, { unlocked: null });
                }}
              >
                🏁 End the game now
              </button>
            )}
            <button
              type="button"
              className="danger"
              onClick={() => {
                setMenu(false);
                if (window.confirm('Throw this game away and start over with new players?')) quit();
              }}
            >
              🗑️ New game, new players
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ setup

function SetupScreen({
  onStart,
}: {
  onStart: (setup: { names: string[]; packCodes: string[]; pointsToWin: number; handSize: number; writeIns: number }) => Promise<void>;
}) {
  const { toast } = usePrefs();
  const [setup, setSetup] = useStoredState<Setup>('cm:onephone:setup', DEFAULT_SETUP, (v) => Array.isArray((v as Setup)?.names));
  const [packs, setPacks] = useState<PackSummary[] | null>(null);
  const [packError, setPackError] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const patch = (p: Partial<Setup>) => setSetup({ ...setup, ...p });

  useEffect(() => {
    api
      .packs()
      .then((list) => {
        setPacks(list);
        // First visit: start with the default packs.
        if (!setup.packCodes.length) setSetup((cur) => ({ ...cur, packCodes: list.filter((p) => p.isDefault).map((p) => p.code) }));
      })
      .catch((err: Error) => setPackError(err.message));
  }, []);

  const all = useMemo(() => {
    const list = [...(packs ?? [])];
    for (const p of setup.extraPacks) if (!list.some((x) => x.code === p.code)) list.push(p);
    return list;
  }, [packs, setup.extraPacks]);

  const chosen = all.filter((p) => setup.packCodes.includes(p.code));
  const answers = chosen.reduce((n, p) => n + p.answerCount, 0);
  const prompts = chosen.reduce((n, p) => n + p.promptCount, 0);
  const filled = setup.names.filter((n) => n.trim()).length;

  function toggle(p: PackSummary) {
    const on = setup.packCodes.includes(p.code);
    if (!on && p.nsfw && !window.confirm(`${p.name} is for adults only. Is everyone playing 18+ and up for it?`)) return;
    patch({ packCodes: on ? setup.packCodes.filter((c) => c !== p.code) : [...setup.packCodes, p.code] });
  }

  async function addByCode(e: React.FormEvent) {
    e.preventDefault();
    const value = code.trim().toUpperCase();
    if (!value) return;
    try {
      const pack = await api.pack(value);
      const { cards: _cards, ...summary } = pack;
      patch({ extraPacks: [...setup.extraPacks.filter((p) => p.code !== summary.code), summary], packCodes: [...new Set([...setup.packCodes, summary.code])] });
      setCode('');
      toast(`${pack.name} added.`, 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not find that pack.', 'error');
    }
  }

  async function start() {
    const checked = checkNames(setup.names);
    if ('error' in checked) return toast(checked.error, 'error');
    if (!chosen.length) return toast('Pick at least one pack.', 'error');
    setBusy(true);
    try {
      await onStart({ names: checked.names, packCodes: chosen.map((p) => p.code), pointsToWin: setup.pointsToWin, handSize: setup.handSize, writeIns: setup.writeIns });
      trackEvent('minigame_started', { game: 'one-phone', players: checked.names.length });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not start the game.', 'error');
      setBusy(false);
    }
  }

  return (
    <div className="game mg">
      <header className="game-header">
        <div className="inner">
          <div className="row between mg-bar" style={{ flexWrap: 'nowrap' }}>
            <Link to="/games" className="btn btn-ghost btn-sm">
              ← Games
            </Link>
            <strong className="mg-title">One phone</strong>
            <span style={{ width: 40 }} />
          </div>
        </div>
      </header>
      <main className="game-main mg-main">
        <section className="mg-intro">
          <span className="mg-emoji" aria-hidden="true">
            {info.emoji}
          </span>
          <h1>{info.name}</h1>
          <p className="muted">{info.tagline}</p>
        </section>
        <details className="panel mg-rules">
          <summary>How to play</summary>
          <ol>
            {info.rules.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ol>
        </details>

        <section className="panel stack">
          <div className="row between">
            <h2 style={{ margin: 0 }}>Players</h2>
            <span className="muted small">
              {ONE_PHONE_MIN} to {ONE_PHONE_MAX} players
            </span>
          </div>
          {setup.names.map((name, i) => (
            <div key={i} className="row" style={{ flexWrap: 'nowrap' }}>
              <span className="mg-seat" aria-hidden="true">
                {i + 1}
              </span>
              <input
                className="input"
                value={name}
                maxLength={NAME_MAX}
                placeholder={['Dadi', 'Bilal', 'Pinky', 'Chachu', 'Asha'][i] ?? 'Name'}
                aria-label={`Player ${i + 1} name`}
                autoComplete="off"
                onChange={(e) => patch({ names: setup.names.map((n, j) => (j === i ? e.target.value : n)) })}
              />
              {setup.names.length > ONE_PHONE_MIN && (
                <button type="button" className="icon-btn" aria-label={`Remove player ${i + 1}`} onClick={() => patch({ names: setup.names.filter((_, j) => j !== i) })}>
                  ✕
                </button>
              )}
            </div>
          ))}
          {setup.names.length < ONE_PHONE_MAX && (
            <button type="button" className="btn btn-outline btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => patch({ names: [...setup.names, ''] })}>
              + Add a player
            </button>
          )}
        </section>

        <section className="panel stack">
          <div className="row between">
            <h2 style={{ margin: 0 }}>Card packs</h2>
            <span className="muted small">
              {prompts} prompts · {answers} answers
            </span>
          </div>
          {packError && <p className="notice">{packError}</p>}
          {!packs && !packError && <p className="muted">Loading packs...</p>}
          <div className="pack-list">
            {all.map((p) => (
              <button key={p.code} type="button" className="pack-option" aria-pressed={setup.packCodes.includes(p.code)} onClick={() => toggle(p)}>
                <span className="tick">{setup.packCodes.includes(p.code) ? '✓' : ''}</span>
                <span className="grow">
                  <span className="row" style={{ gap: 6 }}>
                    <strong>{p.name}</strong>
                    {p.nsfw && <span className="chip red">18+</span>}
                    {p.kind === 'community' && <span className="chip orange">custom · {p.code}</span>}
                  </span>
                  <span className="muted small" style={{ display: 'block' }}>
                    {p.description || `${p.promptCount} prompts, ${p.answerCount} answers`}
                  </span>
                </span>
              </button>
            ))}
          </div>
          <form className="row" style={{ flexWrap: 'nowrap' }} onSubmit={addByCode}>
            <input className="input" placeholder="Add a custom pack by code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={12} aria-label="Custom pack code" />
            <button className="btn btn-blue" disabled={!code.trim()}>
              Add
            </button>
          </form>
        </section>

        <section className="panel stack">
          <h2 style={{ margin: 0 }}>Settings</h2>
          <Choice
            label="Points to win"
            value={setup.pointsToWin}
            options={[
              [5, '5'],
              [7, '7'],
              [10, '10'],
              [0, 'No limit'],
            ]}
            onChange={(pointsToWin) => patch({ pointsToWin })}
          />
          <Choice label="Cards in hand" value={setup.handSize} options={[5, 7, 10].map((n) => [n, String(n)])} onChange={(handSize) => patch({ handSize })} />
          <Choice
            label="Blank cards each"
            value={setup.writeIns}
            options={[
              [0, 'None'],
              [1, '1'],
              [2, '2'],
            ]}
            onChange={(writeIns) => patch({ writeIns })}
          />
        </section>
      </main>
      <div className="action-bar">
        <div className="inner">
          <button type="button" className="btn btn-primary btn-lg btn-block" disabled={busy} onClick={start}>
            {busy ? 'Shuffling...' : filled < ONE_PHONE_MIN ? `Add ${ONE_PHONE_MIN - filled} more ${ONE_PHONE_MIN - filled === 1 ? 'player' : 'players'}` : `Deal the cards (${filled} players)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ turns

function judgeOf(state: RoomState) {
  return state.players.find((p) => p.id === state.round?.judgeId) ?? null;
}

function Intro({ state, judge, onGo }: { state: RoomState; judge: PlayerState | null; onGo: () => void }) {
  const round = state.round!;
  return (
    <>
      <main className="game-main mg-main">
        {round.notice && <p className="notice">{round.notice}</p>}
        <p className="status-line center" style={{ margin: 0 }}>
          👑 {judge?.name ?? 'The judge'} is the judge this round
        </p>
        <PromptCard card={round.prompt} className="pop-in" />
        <p className="muted center" style={{ margin: 0 }}>
          {judge?.name ?? 'Judge'}, read it out loud. Then the phone goes round so everyone can play a card.
        </p>
      </main>
      <div className="action-bar">
        <div className="inner">
          <button type="button" className="btn btn-primary btn-lg btn-block" onClick={onGo}>
            Start passing the phone
          </button>
        </div>
      </div>
    </>
  );
}

function Handoff({ state, player, role, onUnlock }: { state: RoomState; player: PlayerState; role: 'play' | 'judge'; onUnlock: () => void }) {
  const round = state.round!;
  const players = state.players.filter((p) => p.id !== round.judgeId);
  const played = round.submissions.length;
  return (
    <>
      <main className="game-main mg-main">
        <section className="mg-pass pop-in">
          <Avatar player={{ avatar: player.avatar, connected: true, isHost: false }} size="lg" />
          <span className="muted small">{role === 'judge' ? 'Everyone has played' : `${played} of ${players.length} cards in`}</span>
          <h1>Pass the phone to {player.name}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {role === 'judge' ? `${player.name}, you're the judge. Flip the cards and crown a winner.` : `Only ${player.name} looks. Everyone else, eyes off the screen.`}
          </p>
        </section>
      </main>
      <div className="action-bar">
        <div className="inner">
          <button type="button" className="btn btn-primary btn-lg btn-block" onClick={onUnlock}>
            {role === 'judge' ? `I'm ${player.name}, let's judge` : `I'm ${player.name}, show my cards`}
          </button>
        </div>
      </div>
    </>
  );
}

function PlayTurn({
  state,
  player,
  onSubmit,
  onRedraw,
  onCover,
}: {
  state: RoomState;
  player: PlayerState;
  onSubmit: (choices: SubmitChoice[]) => Promise<unknown>;
  onRedraw: () => Promise<unknown>;
  onCover: () => void;
}) {
  const round = state.round!;
  const pick = round.prompt.pick;
  const judge = judgeOf(state);
  const [picks, setPicks] = useState<({ kind: 'card'; card: CardData } | { kind: 'write'; text: string })[]>([]);
  const [writing, setWriting] = useState(false);
  const [sending, setSending] = useState(false);
  const writeInsUsed = picks.filter((p) => p.kind === 'write').length;

  function toggle(card: CardData) {
    setPicks((current) => {
      const at = current.findIndex((p) => p.kind === 'card' && p.card.id === card.id);
      if (at >= 0) return current.filter((_, i) => i !== at);
      if (current.length < pick) return [...current, { kind: 'card', card }];
      if (pick === 1) return [{ kind: 'card', card }];
      return current;
    });
  }

  const orderOf = (card: CardData) => {
    const i = picks.findIndex((p) => p.kind === 'card' && p.card.id === card.id);
    return i >= 0 ? i + 1 : undefined;
  };

  return (
    <>
      <main className="game-main mg-main">
        <div className="row between" style={{ flexWrap: 'nowrap' }}>
          <span className="status-line">{player.name}'s turn</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCover}>
            🙈 Hide
          </button>
        </div>
        <PromptCard card={round.prompt} answers={picks.length ? picks.map((p) => (p.kind === 'card' ? p.card.text : p.text)) : undefined} />
        <div className="row between">
          <strong>
            {pick > 1 ? `Pick ${pick} cards, in order` : `Make ${judge?.name ?? 'the judge'} laugh`}
            {picks.length > 0 && <span className="muted"> · {picks.length}/{pick}</span>}
          </strong>
          <div className="row">
            {player.writeInsLeft - writeInsUsed > 0 && picks.length < pick && (
              <button type="button" className="btn btn-sm btn-outline" onClick={() => setWriting(true)}>
                ✍️ Write my own ({player.writeInsLeft - writeInsUsed})
              </button>
            )}
            {player.redrawsLeft > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  if (!window.confirm(`Swap your whole hand for new cards? You have ${player.redrawsLeft} swap${player.redrawsLeft === 1 ? '' : 's'} left.`)) return;
                  setPicks([]);
                  onRedraw();
                }}
              >
                🔄 Swap hand
              </button>
            )}
          </div>
        </div>
        {picks.some((p) => p.kind === 'write') && (
          <div className="stack" style={{ gap: 8 }}>
            {picks.map((p, i) =>
              p.kind === 'write' ? (
                <AnswerCard
                  key={`w${i}`}
                  card={{ id: `w${i}`, type: 'answer', text: p.text, pick: 1, writeIn: true }}
                  selected
                  order={pick > 1 ? i + 1 : undefined}
                  onClick={() => setPicks((cur) => cur.filter((_, j) => j !== i))}
                />
              ) : null,
            )}
          </div>
        )}
        <div className="hand">
          {player.hand.map((card) => (
            <AnswerCard key={card.id} card={card} selected={Boolean(orderOf(card))} order={pick > 1 ? orderOf(card) : undefined} onClick={() => toggle(card)} />
          ))}
        </div>
      </main>
      <div className="action-bar">
        <div className="inner">
          <button
            type="button"
            className="btn btn-primary btn-lg btn-block"
            disabled={picks.length !== pick || sending}
            onClick={async () => {
              setSending(true);
              await onSubmit(picks.map((p) => (p.kind === 'card' ? { cardId: p.card.id } : { writeIn: p.text })));
              setSending(false);
            }}
          >
            {picks.length === pick ? `Play ${pick > 1 ? 'these cards' : 'this card'} and pass` : `Choose ${pick - picks.length} more`}
          </button>
        </div>
      </div>
      {writing && (
        <WriteIn
          onClose={() => setWriting(false)}
          onSave={(text) => {
            setPicks((cur) => (pick === 1 ? [{ kind: 'write', text }] : [...cur, { kind: 'write' as const, text }].slice(0, pick)));
            setWriting(false);
          }}
        />
      )}
    </>
  );
}

function WriteIn({ onClose, onSave }: { onClose: () => void; onSave: (text: string) => void }) {
  const [text, setText] = useState('');
  return (
    <Sheet title="Write your own card" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) onSave(text.trim());
        }}
      >
        <textarea className="input" autoFocus maxLength={WRITE_IN_MAX} value={text} onChange={(e) => setText(e.target.value)} placeholder="Chachu's 40-minute voice note" />
        <span className="muted small">
          {text.length}/{WRITE_IN_MAX}
        </span>
        <button className="btn btn-primary btn-lg" disabled={!text.trim()}>
          Use this card
        </button>
      </form>
    </Sheet>
  );
}

function JudgeTurn({ state, judge, onReveal, onPick }: { state: RoomState; judge: PlayerState; onReveal: (id: string) => void; onPick: (id: string) => void }) {
  const round = state.round!;
  const [chosen, setChosen] = useState<string | null>(null);
  const allRevealed = round.submissions.every((s) => s.revealed);
  const chosenSub = round.submissions.find((s) => s.id === chosen);
  return (
    <>
      <main className="game-main mg-main">
        <span className="status-line">{allRevealed ? `${judge.name}, tap your favourite, then crown it` : `${judge.name}, flip each card and read it out loud`}</span>
        <PromptCard card={round.prompt} answers={chosenSub ? chosenSub.cards.map((c) => c.text) : undefined} />
        <div className={`submissions ${round.prompt.pick > 1 ? 'multi' : ''}`}>
          {round.submissions.map((sub) => {
            const onClick = sub.revealed ? () => setChosen(sub.id) : () => onReveal(sub.id);
            return (
              <div key={sub.id} className="submission">
                {!sub.revealed ? (
                  <FaceDownCard onClick={onClick} />
                ) : sub.cards.length === 1 ? (
                  <AnswerCard card={sub.cards[0]} selected={chosen === sub.id} onClick={onClick} className="flip-in" />
                ) : (
                  <div className="combo" onClick={onClick} role="button" tabIndex={0}>
                    {sub.cards.map((c, i) => (
                      <AnswerCard key={c.id} card={c} selected={chosen === sub.id} order={i + 1} className="flip-in" />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
      <div className="action-bar">
        <div className="inner">
          {allRevealed ? (
            <button type="button" className="btn btn-primary btn-lg btn-block" disabled={!chosen} onClick={() => chosen && onPick(chosen)}>
              {chosen ? '👑 Crown this one' : 'Tap a card to choose'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-outline btn-lg btn-block"
              onClick={() => {
                const next = round.submissions.find((s) => !s.revealed);
                if (next) onReveal(next.id);
              }}
            >
              Flip the next card
            </button>
          )}
        </div>
      </div>
    </>
  );
}

function RoundOver({ state, onNext }: { state: RoomState; onNext: () => void }) {
  const round = state.round!;
  const winners = round.submissions.filter((s) => round.winnerSubmissionIds.includes(s.id));
  const sorted = [...round.submissions].sort((a, b) => Number(winners.includes(b)) - Number(winners.includes(a)));
  const nextJudge = state.players[(state.players.findIndex((p) => p.id === round.judgeId) + 1) % state.players.length];
  return (
    <>
      <main className="game-main mg-main">
        {winners.length > 0 ? (
          <div className="winner-banner pop-in">
            <div>🏆 Round winner</div>
            <div className="who">{winners.map((w) => w.playerName).join(' & ')}</div>
          </div>
        ) : (
          <p className="notice">{round.notice ?? 'No winner this round.'}</p>
        )}
        {winners[0] ? <PromptCard card={round.prompt} answers={winners[0].cards.map((c) => c.text)} /> : <PromptCard card={round.prompt} />}
        {sorted.length > 0 && <h3 style={{ marginBottom: 0 }}>Everyone's cards</h3>}
        <div className="stack">
          {sorted.map((sub) => (
            <div key={sub.id} className={`submission recap-item ${winners.includes(sub) ? 'winner' : ''}`}>
              <div className="by">
                {winners.includes(sub) && '🏆'} {sub.playerName}
              </div>
              <FilledPrompt prompt={round.prompt} answers={sub.cards.map((c) => c.text)} />
            </div>
          ))}
        </div>
      </main>
      <div className="action-bar">
        <div className="inner">
          <button type="button" className="btn btn-primary btn-lg btn-block" onClick={onNext}>
            Next round: {nextJudge?.name ?? 'next'} judges →
          </button>
        </div>
      </div>
    </>
  );
}

function GameOverScreen({ state, onAgain, onNew }: { state: RoomState; onAgain: () => void; onNew: () => void }) {
  const ranked = [...state.players].sort((a, b) => b.score - a.score);
  const top = ranked[0]?.score ?? 0;
  const champions = ranked.filter((p) => p.score === top && top > 0);
  const podium = [ranked[1], ranked[0], ranked[2]];
  return (
    <>
      <main className="game-main mg-main">
        <section className="center">
          <h1 style={{ marginBottom: 4 }}>{champions.length ? `${champions.map((p) => p.name).join(' & ')} ${champions.length > 1 ? 'win' : 'wins'}!` : 'Game over'}</h1>
          <p className="muted">Complete masti, as promised.</p>
          <div className="podium">
            {podium.map((p, i) =>
              p ? (
                <div key={p.id} className={`step ${['second', 'first', 'third'][i]}`}>
                  <Avatar player={{ avatar: p.avatar, connected: true, isHost: false }} size="lg" />
                  <span>{p.name}</span>
                  <div className="block">{p.score}</div>
                </div>
              ) : (
                <div key={i} className="step" />
              ),
            )}
          </div>
        </section>
        {ranked.length > 3 && (
          <section className="panel">
            <ul className="player-list">
              {ranked.slice(3).map((p, i) => (
                <li key={p.id}>
                  <strong>{i + 4}</strong>
                  <Avatar player={{ avatar: p.avatar, connected: true, isHost: false }} />
                  <span className="name">{p.name}</span>
                  <strong>{p.score}</strong>
                </li>
              ))}
            </ul>
          </section>
        )}
        {state.recap.length > 0 && (
          <section className="stack">
            <h2 style={{ margin: 0 }}>Best of the night</h2>
            {state.recap.map((r) => (
              <div key={r.number} className="recap-item stack" style={{ gap: 6 }}>
                <FilledPrompt prompt={r.prompt} answers={r.winners[0]?.cards.map((c) => c.text) ?? []} />
                <span className="muted small">
                  Round {r.number} · {r.winners.map((w) => w.playerName).join(' & ')}
                </span>
              </div>
            ))}
          </section>
        )}
        <Link to="/" className="center small">
          Want everyone on their own phone? Start a room instead.
        </Link>
      </main>
      <div className="action-bar">
        <div className="inner">
          <button type="button" className="btn btn-outline btn-lg" onClick={onNew}>
            New players
          </button>
          <button type="button" className="btn btn-primary btn-lg grow" onClick={onAgain}>
            Play again
          </button>
        </div>
      </div>
    </>
  );
}
